/* The register vocabulary - one set of table primitives for the list-and-detail screens.

   **Why this file exists.** KD, 2026-10-25, looking at 50 sites, 293 meters and 337 expected
   sources: *"it looks like a blog, not SaaS"*. The registers were stacks of rounded cards and
   `<ul>`s with a hairline under every row, which is what a list of three looks like and is
   unreadable at 337. A register is read by scanning down a column, so these are rows on a shared
   column template, with a header, tabular figures and one hairline between rows.

   **Semantics are the markup's, not this file's.** A `<ul>` stays a list and its `<li>` stays a
   list item; only the layout is a grid, so no screen reader or test that knew it as a list loses
   it. The header row is `aria-hidden` for the same reason: a list does not have columns, and
   announcing "Owner, Billed by" in front of a list item reads as noise.

   **Columns are set per use**, by `--cols` on the row and the header (`style={{"--cols": ...}}`),
   so the two cannot disagree: they read one custom property from one parent.

   Nothing here is a colour literal (ADR-0007) and nothing is under `--t-min`. */

.table {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  min-width: 0;
}

/* Column labels. Muted and semibold rather than small caps: hierarchy by weight and colour, not by
   shrinking the type (33-ui-build-spec). Sticky so a long register keeps its columns named. */
.head {
  position: sticky;
  top: 0;
  z-index: 1;
  display: grid;
  grid-template-columns: var(--cols, minmax(0, 1fr));
  column-gap: 16px;
  align-items: center;
  padding: 7px 14px;
  background: var(--panel-2);
  border-bottom: 1px solid var(--line);
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--muted);
}

.row {
  display: grid;
  grid-template-columns: var(--cols, minmax(0, 1fr));
  gap: 6px 16px;
  align-items: center;
  min-height: 40px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--line);
  font-size: var(--t-dense);
  color: var(--ink);
  min-width: 0;
}

.row:hover {
  background: var(--panel-2);
}

/* A row that carries an open form or a trail is the thing being worked on, not a row to scan past:
   it stops reacting to hover so the form's own controls are not painted over. */
.rowOpen:hover {
  background: none;
}

/* Anything that spans the whole row: an open form, a trail, a sentence about the row. */
.full {
  grid-column: 1 / -1;
  min-width: 0;
}

.cell {
  min-width: 0;
}

.name {
  display: block;
  font-size: var(--t-dense);
  font-weight: 600;
  line-height: 1.35;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.sub {
  display: block;
  font-size: var(--t-min);
  line-height: 1.4;
  color: var(--muted);
}

.muted {
  color: var(--muted);
}

/* Figures: tabular, and right-aligned so a column of them lines up by the decimal point. */
.num {
  font-variant-numeric: lining-nums tabular-nums;
  text-align: right;
  white-space: nowrap;
}

.mono {
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--ink-2);
  overflow-wrap: anywhere;
}

/* The row's own controls, right-aligned in the last column. */
.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 4px 14px;
}

.act {
  /* WCAG 2.5.8 (gate 37): a row action beside another is a 24px target, not the 17px its text is. */
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: var(--t-min);
  font-weight: 500;
  color: var(--accent);
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
}

.act:hover {
  color: var(--accent-h);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.act:disabled {
  color: var(--disabled);
  cursor: default;
  text-decoration: none;
}

/* ---------------------------------------------------------------------------- status pills */

/* A word in a pill. The word carries the meaning and the tint only ranks it (design rule 8), and
   the tints are the status ramp, never the brand green: an emissions screen does not get to
   decorate a state as good news. */
.pill {
  display: inline-block;
  padding: 1px 8px;
  border: 1px solid var(--line-2);
  border-radius: 999px;
  background: var(--panel-2);
  font-size: var(--t-min);
  font-weight: 600;
  line-height: 1.5;
  color: var(--ink-2);
  white-space: nowrap;
}

.pillOk {
  border-color: var(--ok-line);
  background: var(--ok-w);
  color: var(--ok);
}

.pillWarn {
  border-color: var(--warn-line);
  background: var(--warn-w);
  color: var(--warn);
}

.pillCrit {
  border-color: var(--crit-line);
  background: var(--crit-w);
  color: var(--crit);
}

.pillInfo {
  border-color: var(--info-line);
  background: var(--info-w);
  color: var(--info);
}

/* ---------------------------------------------------------------------------- the toolbar */

/* Filters and the ceiling, above a register. Chips are toggles (`aria-pressed`), because "all" is
   a state a reader returns to. */
.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--line);
}

.filter {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 3px 10px;
  border: 1px solid var(--line-2);
  border-radius: 999px;
  background: var(--panel);
  font: inherit;
  font-size: var(--t-min);
  font-weight: 500;
  color: var(--ink-2);
  cursor: pointer;
}

.filter:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.filter[aria-pressed="true"] {
  border-color: var(--accent-line);
  background: var(--accent-w);
  color: var(--accent);
  font-weight: 600;
}

.filterCount {
  font-variant-numeric: lining-nums tabular-nums;
  color: var(--muted);
}

.filter[aria-pressed="true"] .filterCount {
  color: var(--accent);
}

.toolbarNote {
  margin-left: auto;
  font-size: var(--t-min);
  font-variant-numeric: lining-nums tabular-nums;
  color: var(--muted);
}

/* The ceiling a reader raises: said, with the control beside it, under the register. */
.ceiling {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  padding: 9px 14px;
  font-size: var(--t-min);
  font-variant-numeric: lining-nums tabular-nums;
  color: var(--muted);
}

/* ---------------------------------------------------------------------------- small print */

/* A caveat is small print: muted, capped measure, never amber unless it needs attention. */
.note {
  margin: 0;
  max-width: 92ch;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--muted);
}

.noteInk {
  color: var(--ink-2);
}

/* Said once above a register, under the panel title. */
.lead {
  margin: 0;
  padding: 9px 14px;
  border-bottom: 1px solid var(--line);
  max-width: none;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--muted);
}

.lead > span {
  display: block;
  max-width: 92ch;
}

/* A form that sits under a register rather than in a card of its own. */
.under {
  padding: 12px 14px;
  border-top: 1px solid var(--line);
}

/* ---------------------------------------------------------------------------- narrow */

@media (max-width: 900px) {
  .head {
    display: none;
  }

  .row {
    grid-template-columns: minmax(0, 1fr);
    padding: 10px 12px;
  }

  .num {
    text-align: left;
  }

  .actions {
    justify-content: flex-start;
  }

  .toolbarNote {
    margin-left: 0;
  }
}
